import { CircularProgress } from '@mui/material'
// Stripe is loaded dynamically only when needed to avoid blocking initial page load
// import { loadStripe } from '@stripe/stripe-js'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import {
  ConfirmSlotChoosePaymentComponent,
  MentorDetailSection,
  PayUsingGiftCardComponent,
  PaymentConfirmationComponent,
} from 'src/consumer/components/EventBooking/BookingComponents'
import { PAGE_VIEW_STATES } from 'src/consumer/components/EventBooking/bookingConstants'
import {
  getBookingPlanData,
  getProfessionalData,
  payAndMakeEventBookingV2,
} from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import {
  ActionButton,
  ActionButtonsContainer,
} from 'src/consumer/components/EventBooking/EventBookingStyles'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import {
  WebinarBookingAndPaymentContainer,
  WebinarDetailsContainer,
  WebinarDetailsContainerTwo,
  WebinarGiftCodeInputContainer,
} from '../BookingWidgetStyles'

const BookingWebinarPaymentPage = ({ eventDetails, allEvents, closeDialog, timeZone }) => {
  const { profileData: userProfileData } = useContext(ProfileContext)
  const [professionalData, setProfessionalData] = useState({})
  const [pageView, setPageView] = useState(PAGE_VIEW_STATES?.confirm_slot_choose_payment)
  const [confirmBookingLoading, setConfirmBookingLoading] = useState(false)
  const [chosenSlotData, setChosenSlotData] = useState({
    data: { startTime: eventDetails?.startTime, endTime: eventDetails?.endTime },
    preBufferMins: 0,
    postBufferMins: 0,
  })
  const [giftCode, setGiftCode] = useState()
  const [giftCodeError, setGiftCodeError] = useState(false)
  const [stripePromise, setStripePromise] = useState(null)
  const [planData, setPlanData] = useState()
  const [loadingPage, setLoadingPage] = useState(true)

  const eventDuration = dayjs(eventDetails?.endTime).diff(
    dayjs(eventDetails?.startTime),
    'minutes',
  )

  const mentorId = professionalData?.id

  function showBookingConfirmationPage() {
    setPageView(PAGE_VIEW_STATES.bookingConfirmation)
  }

  async function payAndMakeBooking(paymentGateway, giftCardId) {
    setConfirmBookingLoading(true)
    await payAndMakeEventBookingV2(
      eventDetails, // bookingEventDetails
      chosenSlotData, // chosenSlotData
      paymentGateway, // paymentGateway
      giftCardId, // giftCardId
      stripePromise, // stripePromise
      mentorId, // mentorId
      userProfileData, // userProfileData
      setGiftCodeError, // giftCodeErrorSetter
      showBookingConfirmationPage, // afterConfirmationFunction
      'id',
      '/workplace/bookings',
    )
    setConfirmBookingLoading(false)
  }

  async function getTeacherData(mentorSlug) {
    const resp = await getProfessionalData(mentorSlug)
    setProfessionalData(resp)
  }

  async function getPlanData() {
    const planDataToUse = await getBookingPlanData(allEvents, eventDetails?.id, 'id')
    setPlanData(planDataToUse)
  }

  async function setup() {
    setLoadingPage(true)
    const promises = [getPlanData(), getTeacherData(eventDetails?.mentorSlug)]
    await Promise.all(promises)
    setLoadingPage(false)
  }

  function handleGoToBookings() {
    closeDialog()
  }

  useEffect(() => {
    setup()
    
    // Load Stripe only when needed (lazy load) to avoid blocking initial page render
    const loadStripeOnDemand = async () => {
      try {
        // Dynamically import Stripe only when needed
        const { loadStripe } = await import('@stripe/stripe-js')
        const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
        const result = await stripePromise
        setStripePromise(result)
      } catch (error) {
        console.error('Error loading Stripe:', error)
        // Stripe will be loaded on-demand if this fails
      }
    }
    
    // Delay Stripe loading until after initial render using requestIdleCallback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadStripeOnDemand, { timeout: 2000 })
    } else {
      // Fallback: load after a delay
      setTimeout(loadStripeOnDemand, 1000)
    }
  }, [])

  useEffect(() => {
    console.log({ planData })
  }, [planData])

  return loadingPage ? (
    <WebinarBookingAndPaymentContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: '#4a4a4a' }} />
    </WebinarBookingAndPaymentContainer>
  ) : (
    <WebinarBookingAndPaymentContainer>
      {pageView !== PAGE_VIEW_STATES?.bookingConfirmation && (
        <MentorDetailSection
          bookingEvent={eventDetails}
          pageView={pageView}
          planData={planData}
          chosenPurchaseTypeId="single"
          professionalData={professionalData}
          rateText="Price"
          ContainerComponent={WebinarDetailsContainer}
          eventDuration={eventDuration}
          EventDetailsContainerStyledComponent={WebinarDetailsContainerTwo}
        />
      )}
      {pageView === PAGE_VIEW_STATES?.confirm_slot_choose_payment && (
        <ConfirmSlotChoosePaymentComponent
          bookingEvent={eventDetails}
          //   chosenSlotData={chosenSlotData}
          confirmBookingLoading={confirmBookingLoading}
          chosenPurchaseTypeId="single"
          payAndMakeBooking={payAndMakeBooking}
          planData={planData}
          setChosenSlotData={() => {}}
          setPageView={setPageView}
          userProfileData={userProfileData}
          goBackToCalendarButtonFunction={closeDialog}
        />
      )}
      {pageView === PAGE_VIEW_STATES?.payUsingGiftCard && (
        <PayUsingGiftCardComponent
          confirmBookingLoading={confirmBookingLoading}
          giftCode={giftCode}
          setGiftCode={setGiftCode}
          giftCodeError={giftCodeError}
          setGiftCodeError={setGiftCodeError}
          payAndMakeBooking={payAndMakeBooking}
          setPageView={setPageView}
          CustomGiftCodeInputContainer={WebinarGiftCodeInputContainer}
        />
      )}
      {pageView === PAGE_VIEW_STATES?.bookingConfirmation && (
        <PaymentConfirmationComponent
          bookingEventDetails={eventDetails}
          fromOnboarding={false}
          professionalData={professionalData}
          chosenSlotData={{
            data: { startTime: eventDetails?.startTime, endTime: eventDetails?.endTime },
          }}
          customActionButtonComponent={
            <ActionButtonsContainer>
              <ActionButton suggested onClick={handleGoToBookings}>
                Go to Webinar Bookings
              </ActionButton>
            </ActionButtonsContainer>
          }
        />
      )}
    </WebinarBookingAndPaymentContainer>
  )
}

export default BookingWebinarPaymentPage
